import { createReducer, on } from '@ngrx/store';
import { first } from 'lodash';

import {
  addLatestAnyNotification,
  addLatestNotification,
  clearAnyNotifications,
  clearNotificationsStore,
  deleteNotification,
  deleteNotificationFailed,
  deleteNotificationSuccess,
  getLastNotificationList,
  getLastNotificationListFailed,
  getLastNotificationListSuccess,
  markAllAsRead,
  markAllAsReadFailed,
  markAllAsReadSuccess,
  markAsRead,
  markAsReadFailed,
  markAsReadSuccess,
  updateAnyNotifications,
} from '@main-application/notifications/store/actions/notifications.actions';
import { UpdateStatus } from '@shared/enums/update-status';
import { WebNotification } from '@shared/interfaces/notification.interface';

export const notificationsFeatureKey = 'notifications';

export interface State {
  bidNotification: WebNotification;
  ticketNotification: WebNotification;
  turnoverNotification: WebNotification;
  lastAnyNotificationList: WebNotification[];
  lastNotificationList: WebNotification[];
  lastNotificationListLoading: boolean;
  markAllAsReadInProgress: boolean;
  markAllAsReadStatus: UpdateStatus;
  markAsReadInProgress: boolean;
  markAsReadStatus: UpdateStatus;
  markAsReadId: string;
  deleteNotificationInProgress: boolean;
}

export const initialState: State = {
  bidNotification: null,
  ticketNotification: null,
  turnoverNotification: null,
  lastAnyNotificationList: [],
  lastNotificationList: [],
  lastNotificationListLoading: false,
  markAllAsReadInProgress: false,
  markAllAsReadStatus: null,
  markAsReadInProgress: false,
  markAsReadStatus: null,
  markAsReadId: null,
  deleteNotificationInProgress: false,
};

export const reducer = createReducer(
  initialState,
  /**
   * Clear Notifications store
   */
  on(clearNotificationsStore, (state, action) => ({
    ...state,
    ...initialState,
  })),

  /**
   * get last notification list
   */
  on(getLastNotificationList, (state, action) => ({
    ...state,
    lastNotificationListLoading: true,
  })),
  on(getLastNotificationListSuccess, (state, action) => ({
    ...state,
    lastNotificationList: action.lastNotificationList,
    lastNotificationListLoading: false,
  })),
  on(getLastNotificationListFailed, (state, action) => ({
    ...state,
    lastNotificationList: [],
    lastNotificationListLoading: false,
  })),

  on(addLatestNotification, (state, action) => ({
    ...state,
    lastNotificationList: [action.notification, ...state.lastNotificationList],
  })),

  on(addLatestAnyNotification, (state, action) => ({
    ...state,
    lastAnyNotificationList: [action.notification, ...state.lastAnyNotificationList],
  })),

  on(clearAnyNotifications, (state, action) => ({
    ...state,
    lastAnyNotificationList: [],
  })),

  on(updateAnyNotifications, (state, action) => ({
    ...state,
    lastAnyNotificationList: [...action.list],
  })),

  /**
   * mark all as read
   */
  on(markAllAsRead, (state, action) => ({
    ...state,
    markAllAsReadInProgress: true,
    markAllAsReadStatus: null,
  })),
  on(markAllAsReadSuccess, (state, action) => ({
    ...state,
    markAllAsReadInProgress: false,
    markAllAsReadStatus: UpdateStatus.UPDATE,
  })),
  on(markAllAsReadFailed, (state, action) => ({
    ...state,
    markAllAsReadInProgress: false,
    markAllAsReadStatus: UpdateStatus.ERROR,
  })),

  /**
   * mark selected as read
   */
  on(markAsRead, (state, action) => ({
    ...state,
    markAsReadInProgress: true,
    markAsReadStatus: null,
    markAsReadId: first(action.baseNotificationBody?.notificationIds),
  })),
  on(markAsReadSuccess, (state, action) => ({
    ...state,
    markAsReadInProgress: false,
    markAsReadStatus: UpdateStatus.UPDATE,
  })),
  on(markAsReadFailed, (state, action) => ({
    ...state,
    markAsReadInProgress: false,
    markAsReadStatus: UpdateStatus.ERROR,
  })),

  /**
   * delete notification
   */
  on(deleteNotification, (state, action) => ({
    ...state,
    deleteNotificationInProgress: true,
  })),
  on(deleteNotificationSuccess, (state, action) => ({
    ...state,
    deleteNotificationInProgress: false,
  })),
  on(deleteNotificationFailed, (state, action) => ({
    ...state,
    deleteNotificationInProgress: false,
  }))
);
